import type { ReactNode } from 'react';
import { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';

interface AlertsContextType {
  alertsCount: number;
  fetchAlertsCount: () => Promise<void>;
  connectionStatus: 'connected' | 'disconnected' | 'reconnecting';
}

const AlertsContext = createContext<AlertsContextType | undefined>(undefined);

export function AlertsProvider({ children }: { children: ReactNode }) {
  const [alertsCount, setAlertsCount] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'reconnecting'>('disconnected');
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const maxReconnectAttempts = 5;

  const fetchAlertsCount = useCallback(async () => {
    try {
      const { count, error } = await supabase
        .from('emergency_alerts')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'active');
      
      if (error) {
        console.error('Error fetching alerts count:', error);
        return;
      }
      
      setAlertsCount(count || 0);
    } catch (error) {
      console.error('Error fetching alerts count:', error);
    }
  }, []);

  // Enhanced real-time subscription with immediate updates
  useEffect(() => {
    let channel: any;
    let reconnectTimeout: NodeJS.Timeout;

    const setupRealtimeSubscription = () => {
      if (channel) {
        supabase.removeChannel(channel);
      }

      channel = supabase
        .channel('alerts_count_realtime_enhanced')
        .on(
          'postgres_changes',
          { event: 'INSERT', schema: 'public', table: 'emergency_alerts' },
          () => {
            console.log('New alert inserted, updating count immediately');
            // Immediately increment count for faster UI response
            setAlertsCount(prev => prev + 1);
            // Then fetch fresh data to ensure accuracy
            fetchAlertsCount();
          }
        )
        .on(
          'postgres_changes',
          { event: 'UPDATE', schema: 'public', table: 'emergency_alerts' },
          () => {
            console.log('Alert updated, checking count');
            // Fetch fresh count to handle status changes
            fetchAlertsCount();
          }
        )
        .on(
          'postgres_changes',
          { event: 'DELETE', schema: 'public', table: 'emergency_alerts' },
          () => {
            console.log('Alert deleted, updating count');
            // Fetch fresh count to handle deletions
            fetchAlertsCount();
          }
        )
        .subscribe((status) => {
          console.log('Alerts count subscription status:', status);
          
        if (status === 'SUBSCRIBED') {
            setConnectionStatus('connected');
            setReconnectAttempts(0);
          } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            setConnectionStatus('disconnected');
            
            // Attempt to reconnect with exponential backoff
            if (reconnectAttempts < maxReconnectAttempts) {
              setConnectionStatus('reconnecting');
              const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 30000);
              
              reconnectTimeout = setTimeout(() => {
                setReconnectAttempts(prev => prev + 1);
                setupRealtimeSubscription();
              }, delay);
            }
        }
      });
    };

    // Initial setup
    setupRealtimeSubscription();
    fetchAlertsCount();

    // Periodic health check and count refresh
    const healthCheckInterval = setInterval(() => {
      if (connectionStatus === 'disconnected' && reconnectAttempts < maxReconnectAttempts) {
        console.log('Performing health check and attempting reconnection...');
        setupRealtimeSubscription();
      }
      // Also refresh count periodically to ensure accuracy
      fetchAlertsCount();
    }, 30000); // Check every 30 seconds

    return () => {
      if (channel) {
      supabase.removeChannel(channel);
      }
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      clearInterval(healthCheckInterval);
    };
  }, [fetchAlertsCount, connectionStatus, reconnectAttempts]);

  return (
    <AlertsContext.Provider value={{ alertsCount, fetchAlertsCount, connectionStatus }}>
      {children}
    </AlertsContext.Provider>
  );
}

export function useAlerts() {
  const context = useContext(AlertsContext);
  if (context === undefined) {
    throw new Error('useAlerts must be used within an AlertsProvider');
  }
  return context;
} 